import { FC, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import Close from '../../assets/close.png';
import FooterButton from '../button/button';
import { ModalProps } from './modal.type';
import KebabIcon from '../../assets/kebab.png';
import Switch from '../switch/switch';
import DropdownMenu from '../dropDownMenu/dropDownMenu';
import Loading from '../../assets/loading/loading';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import {
  useCloseOnOutsideClickAndEsc,
  getIdAttributes,
} from '../../utils/helper';
import back from '../../assets/back.png';

export const idPrefix: string = 'modal';

const ModalComponent: FC<ModalProps> = (props) => {
  const handleModalClose = () => {
    props.onClose?.();
    props.onToggle(false);
  };

  const modalRef = useRef<HTMLDivElement>(null);

  let outsideClickRef: React.RefObject<HTMLDivElement> | null =
    props.shouldCloseOnOutsideClick ? modalRef : null;
  // Hook: close on outside click or ESC
  useCloseOnOutsideClickAndEsc(outsideClickRef, handleModalClose);

  return (
    <Modal
      show={props.open}
      onHide={handleModalClose}
      backdrop={true}
      // enforceFocus: Set to false when modal contains components that portal outside
      // the modal DOM (e.g., MultiSelectDropdown). Without this, the focus trap steals
      // focus from portaled elements, making inputs untypeable.
      enforceFocus={props.enforceFocus ?? true}
      dialogClassName={`absolute ${
        props.centered
          ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          : 'top-24 right-12'
      } m-0 z-20 bg-white shadow-[-10px_0px_50px_#2F41A51F] rounded-2xl border ${
        props.dialogClassName
      }`}
    >
      <div
        ref={modalRef}
        className={`flex flex-col ${props.modalClassName}`}
        {...getIdAttributes(idPrefix, 'container')}
      >
        {/* Modal Header */}
        <Modal.Header
          className={`${!props.noHeaderBorder && 'border-b'} pb-6 mx-10 py-10 ${props.headerClassName ?? ''}`}
          {...getIdAttributes(idPrefix, 'header')}
        >
          <div className="flex justify-between w-100 items-center">
            <h5 className="m-0 text-[#1A1A1A]">
              {props.showBackButton && (
                <img
                  src={back}
                  alt="back"
                  className="cursor-pointer mt-2"
                  onClick={props.onBackButtonClick}
                  {...getIdAttributes(idPrefix, 'back-button')}
                />
              )}
              <span className="font-semibold">{props.title}</span>
              <span className="font-normal">{props.subTitle}</span>
            </h5>

            <div className="flex">
              {props.showSwitch && (
                <div className="mr-5">
                  {props.activeDeactiveLoading ? (
                    <Loading height={30} width={30} />
                  ) : (
                    <Switch
                      activeLabel={props.activeLabel}
                      setActiveLabel={props.setActiveLabel}
                      onChangeSwitch={() =>
                        props.onChangeSwitch?.(!props.activeLabel)
                      }
                      data-testid={`${idPrefix}-switch`}
                    />
                  )}
                </div>
              )}

              {props.showAction && (
                <div className="relative">
                  <img
                    src={KebabIcon}
                    alt="Kebab icon"
                    className="w-[25px] h-[25px] mr-5 cursor-pointer"
                    onClick={() =>
                      props.handleKebabClick?.(props.rowIndex ?? 0)
                    }
                    {...getIdAttributes(idPrefix, 'kebab-menu')}
                  />
                  {props.menuOpen && (
                    <div className="absolute top-full right-16 z-10">
                      <DropdownMenu
                        className="w-40"
                        options={props.kebabOptions ?? []}
                        onSelect={(action) => {
                          if (
                            props.kebabOptionUser &&
                            props.rowIndex !== undefined
                          ) {
                            props.onKebabSelect?.(
                              action,
                              props.kebabOptionUser,
                              props.rowIndex,
                            );
                          }
                        }}
                        onClose={props.closeMenu}
                        data-testid={props.dropdownMenuTestId}
                      />
                    </div>
                  )}
                </div>
              )}

              {props.isShowClose && (
                <img
                  onClick={handleModalClose}
                  src={Close}
                  alt="Close"
                  className="w-4 h-4 mt-1 cursor-pointer"
                  {...getIdAttributes(idPrefix, 'close-button')}
                />
              )}
            </div>
          </div>
        </Modal.Header>

        {/* Modal Body */}
        <Modal.Body
          className="px-10 pt-4 pb-2 py-10 flex-1 overflow-auto"
          {...getIdAttributes(idPrefix, 'body')}
        >
          {props.children}
        </Modal.Body>

        {/* Modal Footer */}
        {!props.footerHidden && (
          <Modal.Footer
            className={`border-0 pt-12 pb-10 px-10 flex justify-between mt-auto ${props.footerClassName}`}
            {...getIdAttributes(idPrefix, 'footer')}
          >
            <div className={`w-28 ${props.footerButton1WrapperStyle}`}>
              {props.showFooter1Button && (
                <FooterButton
                  className={`w-full py-2 rounded-full bg-white border text-[#000000] text-sm border-[#D8D8D8] hover:bg-white hover:text-[#000000] ${props.footerButton1Style}`}
                  onClick={props.onFooterButton1Click}
                  disabled={props.disableFooter1Button}
                >
                  {props.footerButton1Text}
                </FooterButton>
              )}
            </div>

            <div
              className={`${props.showResendInvitation ? 'w-40' : 'w-28'} ${
                props.footerButton2WrapperStyle
              }`}
            >
              {props.showFooter2Button && (
                <FooterButton
                  className={`w-full py-2 rounded-full text-sm ${props.footerButton2Style}`}
                  onClick={props.onFooterButton2Click}
                  disabled={props.disableFooter2Button}
                >
                  {props.showCreateUserLoading ? (
                    <ButtonSpinner />
                  ) : (
                    props.footerButton2Text
                  )}
                </FooterButton>
              )}
            </div>
          </Modal.Footer>
        )}
      </div>
    </Modal>
  );
};

export default ModalComponent;
